const SESSION_START_KEY = 'bymanay-discount-popup-session-start';
const DISMISSED_KEY = 'bymanay-discount-popup-dismissed';
const SHOWN_KEY = 'bymanay-discount-popup-shown';
const SUBSCRIBED_KEY = 'bymanay-discount-popup-subscribed';

class BYManayDiscountPopup extends HTMLElement {
  #timerId = null;

  connectedCallback() {
    if (window.location.hash === '#BymanayDiscountPopup') {
      window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`);
    }

    this.querySelectorAll('[data-bymanay-discount-popup-close]').forEach((node) => {
      node.addEventListener('click', () => this.#dismiss());
    });
    this.querySelector('[data-bymanay-discount-popup-close-success]')?.addEventListener('click', () => {
      this.#dismiss();
    });

    this.querySelector('[data-bymanay-discount-popup-copy]')?.addEventListener('click', () => this.#copyCode());
    document.addEventListener('keydown', this.#onKeyDown);

    const emailInput = this.querySelector('[data-bymanay-discount-popup-email]');
    const emailWrap = this.querySelector('[data-bymanay-discount-popup-input-wrap]');
    const syncEmailField = () => {
      const active =
        document.activeElement === emailInput || Boolean(emailInput?.value?.trim().length);
      emailWrap?.classList.toggle('is-active', active);
    };
    const unlockEmail = () => {
      emailInput?.removeAttribute('readonly');
      syncEmailField();
    };
    emailInput?.addEventListener('pointerdown', unlockEmail, { once: true });
    emailInput?.addEventListener('focus', () => {
      unlockEmail();
      syncEmailField();
    });
    emailInput?.addEventListener('blur', syncEmailField);
    emailInput?.addEventListener('input', syncEmailField);
    if (emailInput?.value?.trim()) {
      emailInput.removeAttribute('readonly');
      syncEmailField();
    }

    const params = new URLSearchParams(window.location.search);
    const postedSuccess = Boolean(this.querySelector('[data-bymanay-popup-success]'));
    const errorText = this.querySelector('.bymanay-discount-popup__error')?.textContent?.trim() || '';
    const hasFormError = Boolean(errorText);
    const duplicateEmail = /already|taken|ya ha sido|ya está|utilizado|registrad|exists/i.test(errorText);

    if (params.get('popup_subscribed') === '1' || postedSuccess || duplicateEmail) {
      localStorage.setItem(SUBSCRIBED_KEY, '1');
      sessionStorage.setItem(SHOWN_KEY, '1');
      this.#showSuccess();
      this.#openPopup();
      params.delete('popup_subscribed');
      const nextQuery = params.toString();
      const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}`;
      window.history.replaceState({}, '', nextUrl);
      return;
    }

    if (hasFormError) {
      sessionStorage.removeItem(SHOWN_KEY);
      this.#showOffer();
      this.#openPopup();
      return;
    }

    if (localStorage.getItem(SUBSCRIBED_KEY) === '1' || sessionStorage.getItem(DISMISSED_KEY) === '1') {
      this.hidden = true;
      return;
    }

    if (sessionStorage.getItem(SHOWN_KEY) === '1') {
      this.hidden = true;
      return;
    }

    if (!sessionStorage.getItem(SESSION_START_KEY)) {
      sessionStorage.setItem(SESSION_START_KEY, String(Date.now()));
    }

    const delayMs = Math.max(0, Number(this.dataset.delaySeconds || 15) * 1000);
    const tryOpen = () => {
      const started = Number(sessionStorage.getItem(SESSION_START_KEY));
      if (!started || Number.isNaN(started)) return;
      if (Date.now() - started >= delayMs) {
        this.#open();
      }
    };

    tryOpen();
    this.#timerId = window.setInterval(tryOpen, 500);
  }

  disconnectedCallback() {
    if (this.#timerId) window.clearInterval(this.#timerId);
    document.removeEventListener('keydown', this.#onKeyDown);
  }

  #onKeyDown = (event) => {
    if (event.key === 'Escape' && !this.hidden) {
      event.preventDefault();
      this.#dismiss();
    }
  };

  #open() {
    if (this.#timerId) {
      window.clearInterval(this.#timerId);
      this.#timerId = null;
    }
    sessionStorage.setItem(SHOWN_KEY, '1');
    this.#showOffer();
    this.#openPopup();
  }

  #openPopup() {
    this.hidden = false;
    document.documentElement.classList.add('bymanay-discount-popup-open');
  }

  #dismiss() {
    sessionStorage.setItem(DISMISSED_KEY, '1');
    this.hidden = true;
    document.documentElement.classList.remove('bymanay-discount-popup-open');
  }

  #showOffer() {
    const offer = this.querySelector('[data-bymanay-discount-popup-step="offer"]');
    const success = this.querySelector('[data-bymanay-discount-popup-step="success"]');
    offer?.removeAttribute('hidden');
    offer?.classList.remove('hidden');
    success?.setAttribute('hidden', '');
    success?.classList.add('hidden');
  }

  #showSuccess() {
    const offer = this.querySelector('[data-bymanay-discount-popup-step="offer"]');
    const success = this.querySelector('[data-bymanay-discount-popup-step="success"]');
    offer?.setAttribute('hidden', '');
    offer?.classList.add('hidden');
    success?.removeAttribute('hidden');
    success?.classList.remove('hidden');

    this.querySelector('[data-bymanay-discount-popup-copy]')?.removeAttribute('hidden');
    const copied = this.querySelector('[data-bymanay-discount-popup-copied]');
    copied?.classList.add('hidden');
    if (copied) copied.textContent = '';
    this.querySelector('[data-bymanay-discount-popup-close-success]')?.setAttribute('hidden', '');
  }

  async #copyCode() {
    const code = this.dataset.discountCode?.trim();
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = code;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      textarea.remove();
    }

    const copyBtn = this.querySelector('[data-bymanay-discount-popup-copy]');
    const copied = this.querySelector('[data-bymanay-discount-popup-copied]');
    const closeBtn = this.querySelector('[data-bymanay-discount-popup-close-success]');

    copyBtn?.setAttribute('hidden', '');

    if (copied) {
      copied.textContent = copied.dataset.copiedMessage || code;
      copied.classList.remove('hidden');
    }

    closeBtn?.removeAttribute('hidden');
    closeBtn?.focus();
  }
}

if (!customElements.get('bymanay-discount-popup')) {
  customElements.define('bymanay-discount-popup', BYManayDiscountPopup);
}
